import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Icons from "react-feather";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, Button, Modal, ModalBody, ModalFooter, ModalHeader } from "Components/ReactstrapSlim";
import { InputFL, ValidationSummary } from "Components/Inputs";
import { LayoutContext } from "Components/LayoutContext";
import { useShareFull } from "Components/Embedded/Share";
import { CollectionsClient, CollectionType, ICollaboratorViewModel, } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";

export interface CollectionMenuProps {
   collection: { id: string | undefined, name: string | undefined, collaborators: ICollaboratorViewModel[] | undefined, collectionType: CollectionType | undefined, numberOfSpaces: number | undefined },
   collectionUrl?: string | undefined,
   count?: number | undefined,
   renameCallback: (newName: string | undefined) => Promise<void>,
   deleteCallback: (collectionType: CollectionType | undefined) => Promise<void>,
   copyCallback?: (newName: string | undefined) => Promise<void>
}

interface NameForm {
   name: string | undefined
}

interface CopyForm {
   name: string | undefined
}

const NameFormSchema: yup.SchemaOf<NameForm> = yup.object({
   name: yup.string().trim().required("Name is required").defined()
});

const CopyFormSchema: yup.SchemaOf<CopyForm> = yup.object({
   name: yup.string().trim().required("Name is required").defined()
});

export const CollectionMenu: React.FC<CollectionMenuProps> = ({ collection, collectionUrl = "", renameCallback, deleteCallback, copyCallback, count }) => {
   const collectionClient = useApiClient(CollectionsClient);
   const layoutContext = useContext(LayoutContext);
   const share = useShareFull();

   const formRenameMethods = useForm<NameForm>({
      mode: "onTouched",
      resolver: yupResolver(NameFormSchema)
   })

   const formCopyMethods = useForm<CopyForm>({
      mode: "onTouched",
      resolver: yupResolver(CopyFormSchema)
   })

   const [serverError, setServerError] = useState<Array<[string, string]>>([]);
   const [itemToDelete, setItemToDelete] = useState<{ id: string | undefined, name: string | undefined, collectionType: CollectionType | undefined } | null>(null);
   const [itemToRename, setItemToRename] = useState<{ id: string | undefined, name: string | undefined } | null>(null);
   const [itemToCopy, setItemToCopy] = useState<{ id: string | undefined, name: string | undefined } | null>(null);

   const hideDeleteModal = useCallback(async () => {
      setItemToDelete(null);
   }, []);

   const hideRenameModal = useCallback(async () => {
      setItemToRename(null);
   }, []);

   const hideCopyItemModal = useCallback(async () => {
      setItemToCopy(null);
   }, []);

   const renameData = async () => {
      try {
         const isValid = await formRenameMethods.trigger();
         if (isValid && itemToRename?.id != undefined) {
            const data = formRenameMethods.getValues();
            if (data.name != undefined) {
               const result = await collectionClient.renameCollection(itemToRename.id, data.name);

               if (result) {
                  hideRenameModal();
                  renameCallback(data.name);
                  layoutContext.showMessage(`Collection ${itemToRename.name} was successfully renamed to ${data.name}`);
               } else {
                  setServerError([["name", "Unexpected server error"]]);
               }
            }
         }
      } catch (e: any) {
         if (e.errorMessage) {
            setServerError([["name", e.errorMessage]]);
         }
      }
   }

   const copyData = async () => {
      try {
         const isValid = await formCopyMethods.trigger();
         if (isValid && itemToCopy?.id != undefined) {
            const data = formCopyMethods.getValues();
            if (data.name != undefined) {
               const result = await collectionClient.copyCollection(itemToCopy.id, data.name);

               if (result) {
                  hideCopyItemModal();
                  if(copyCallback)
                     copyCallback(data.name);
                  layoutContext.showMessage(`Collection ${itemToCopy.name} was successfully copied to ${data.name}`);
               } else {
                  setServerError([["name", "Unexpected server error"]]);
               }
            }
         }
      } catch (e: any) {
         if (e.errorMessage) {
            setServerError([["name", e.errorMessage]]);
         }
      }
   }

   const removeData = useCallback((id: string | undefined, name: string | undefined, collectionType: CollectionType | undefined) => {
      if(id == undefined)
         return;

      if (collectionType === CollectionType.Shared) {
         collectionClient.deleteSharedCollection(id).then(responseWrapper => {
            hideDeleteModal();
            deleteCallback(collectionType);
            layoutContext.showMessage(`Shared collection ${name} was successfully removed`);
         });
      } else if (collectionType === CollectionType.Regular) {
         collectionClient.deleteCollection(id).then(responseWrapper => {
            hideDeleteModal();
            deleteCallback(collectionType);
            layoutContext.showMessage(`Collection ${name} was successfully removed`);
         });
      }
   }, []);

   const shareCollection = async () => {
      if (collection.id) {
         collectionClient.shareCollectionData(collection.id).then(result => {
            share.openFullShare(result.collectionId, result.collectionOwnerId, result.collectionUrl, result.shareModel);
         }).catch((e) => {
            console.log(e);
         })
      }
   }

   useEffect(() => {
      const abortController = new AbortController();

      return function cleanup() {
         abortController.abort();
      }
   }, []);

   return (
      <>
         <Modal isOpen={itemToDelete != null} toggle={hideDeleteModal}>
            <ModalHeader toggle={hideDeleteModal}>Remove Collection?</ModalHeader>
            <ModalBody className="ls-modal-body">
               Are you sure that you want to remove "{itemToDelete?.name}"?
            </ModalBody>
            <ModalFooter>
               <Button color="secondary" onClick={hideDeleteModal}>Cancel</Button>
               <Button color="primary" onClick={() => removeData(itemToDelete?.id, itemToDelete?.name, itemToDelete?.collectionType)} aria-label="Remove collection" >Ok</Button>
            </ModalFooter>
         </Modal>
         <Modal isOpen={itemToRename != null} toggle={hideRenameModal}>
            <ModalHeader toggle={hideRenameModal}>Rename Collection</ModalHeader>
            <ModalBody className="ls-modal-body">
               <InputFL control={formRenameMethods.control} name="name" defaultValue={itemToRename?.name} placeholder="Collection Name" />
               <ValidationSummary errors={formRenameMethods.formState.errors} serverErrors={serverError} />
            </ModalBody>
            <ModalFooter>
               <Button color="secondary" onClick={hideRenameModal}>Cancel</Button>
               <Button color="primary" onClick={() => renameData()} aria-label="Rename collection" >Ok</Button>
            </ModalFooter>
         </Modal>
         <Modal isOpen={itemToCopy != null} toggle={hideCopyItemModal}>
            <ModalHeader toggle={hideCopyItemModal}>Copy Collection</ModalHeader>
            <ModalBody className="ls-modal-body">
               <InputFL control={formCopyMethods.control} name="name" defaultValue={itemToCopy?.name} placeholder="Collection Name" />
               <ValidationSummary errors={formCopyMethods.formState.errors} serverErrors={serverError} />
            </ModalBody>
            <ModalFooter>
               <Button color="secondary" onClick={hideCopyItemModal}>Cancel</Button>
               <Button color="primary" onClick={() => copyData()} aria-label="Copy collection" >Ok</Button>
            </ModalFooter>
         </Modal>
         {collection.collaborators && collection.collaborators?.length > 0 &&
            <ul className="collaborators">
               {collection.collaborators.slice(0, 5).map(cb => <li key={cb.id} className="collaborators-item" >{cb.pictureURL && <img className="collaborators-img" src={cb.pictureURL} />}{!cb.pictureURL && <span className="collaborators-img lbl1b">{cb.initials}</span>}</li>)}
               {collection.collaborators.length > 5 &&
                  <li className="collaborators-item"><span className="collaborators-plus-count lbl1b">+{collection.collaborators.length - 5}</span></li>
               }
            </ul>
         }
         <Button size="icon" disabled={collection.numberOfSpaces === undefined || collection.numberOfSpaces === 0} color="link" className="rounded share-button icon" onClick={() => shareCollection()} ><Icons.Share2 className="share-icon" size={24} /></Button>
         <UncontrolledDropdown>
            <DropdownToggle color="link" className="rounded" size="icon">
               <Icons.MoreHorizontal className="icon" />
            </DropdownToggle>
            <DropdownMenu right>
               {collectionUrl && <DropdownItem onClick={() => window.open(`${collectionUrl}`, "_blank")}>View Collection Page</DropdownItem>}
               {collection.numberOfSpaces !== undefined && collection.numberOfSpaces > 0 && <DropdownItem onClick={() => setItemToCopy(collection)}>Copy</DropdownItem>}
               {collection.collectionType === CollectionType.Regular && <DropdownItem onClick={() => setItemToRename(collection)}>Rename</DropdownItem>}
               {collection.collectionType === CollectionType.Regular && count && count === 1 ? "" :
                  <DropdownItem onClick={() => setItemToDelete(collection)}>Delete</DropdownItem>
               }
            </DropdownMenu>
         </UncontrolledDropdown>
      </>
   );
}